import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * A single global cart that is ALWAYS tied to exactly one store.
 * Shape persisted to localStorage:
 *   { storeSlug, storeName, currencySymbol, items: [{ product, quantity }] }
 *
 * addItem() refuses to mix stores: if the cart already holds items from a
 * different store it returns { conflict: true, currentStore } so the UI can
 * ask the customer to clear the cart first.
 */

const STORAGE_KEY = 'saanjh_cart';
const CartContext = createContext(null);

const emptyCart = { storeSlug: null, storeName: '', currencySymbol: '₹', items: [] };

function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyCart;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return emptyCart;
    return { ...emptyCart, ...parsed };
  } catch {
    return emptyCart;
  }
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(loadCart);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
    } catch {
      /* storage full / unavailable — cart simply won't persist */
    }
  }, [cart]);

  const clearCart = useCallback(() => setCart(emptyCart), []);

  // store = { slug, name, currencySymbol }
  const addItemForStore = useCallback((product, store) => {
    setCart((prev) => {
      const base =
        prev.storeSlug === store.slug
          ? prev
          : { storeSlug: store.slug, storeName: store.name, currencySymbol: store.currencySymbol || '₹', items: [] };
      const existing = base.items.find((i) => i.product.id === product.id);
      const items = existing
        ? base.items.map((i) =>
            i.product.id === product.id ? { ...i, quantity: i.quantity + 1 } : i
          )
        : [...base.items, { product, quantity: 1 }];
      return { ...base, items };
    });
  }, []);

  const addItem = useCallback(
    (product, store) => {
      // Different store with a non-empty cart → caller must resolve the conflict.
      if (cart.items.length > 0 && cart.storeSlug && cart.storeSlug !== store.slug) {
        return { conflict: true, currentStore: cart.storeName };
      }
      addItemForStore(product, store);
      return { conflict: false };
    },
    [cart, addItemForStore]
  );

  // Clear whatever is in the cart and start fresh with this product.
  const replaceWithItem = useCallback(
    (product, store) => {
      setCart({
        storeSlug: store.slug,
        storeName: store.name,
        currencySymbol: store.currencySymbol || '₹',
        items: [{ product, quantity: 1 }],
      });
    },
    []
  );

  const updateQuantity = useCallback((productId, delta) => {
    setCart((prev) => {
      const items = prev.items
        .map((i) => (i.product.id === productId ? { ...i, quantity: i.quantity + delta } : i))
        .filter((i) => i.quantity > 0);
      return items.length ? { ...prev, items } : emptyCart;
    });
  }, []);

  const removeItem = useCallback((productId) => {
    setCart((prev) => {
      const items = prev.items.filter((i) => i.product.id !== productId);
      return items.length ? { ...prev, items } : emptyCart;
    });
  }, []);

  const totalPrice = cart.items.reduce((sum, i) => sum + i.product.price * i.quantity, 0);
  const totalCount = cart.items.reduce((sum, i) => sum + i.quantity, 0);

  const value = {
    cart,
    items: cart.items,
    storeSlug: cart.storeSlug,
    storeName: cart.storeName,
    addItem,
    replaceWithItem,
    updateQuantity,
    removeItem,
    clearCart,
    totalPrice,
    totalCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
}
